// stores/permission.js
import { defineStore } from 'pinia';
import axios from '@/axios'; // Import axios
import { clearStoredLocation, persistLocation } from '@/utils/locationResolver';

const storedConsent = localStorage.getItem('hasConsented');
localStorage.removeItem('latitude');
localStorage.removeItem('longitude');

export const usePermissionStore = defineStore('permission', {
  state: () => ({
    hasConsented: storedConsent === null ? null : storedConsent === 'true',
    locationPromptActive: false,
    cityName: localStorage.getItem('cityName') || null,
    cityCode: localStorage.getItem('cityCode') || null,
    countryCode: localStorage.getItem('countryCode') || null,
    latitude: localStorage.getItem('latitude') || null,
    longitude: localStorage.getItem('longitude') || null,
    currencyData: localStorage.getItem('currencyData') || null,
    nearbyCities: JSON.parse(localStorage.getItem('nearbyCities')) || [],
  }),
  actions: {
    setLocationPromptActive(value) {
      this.locationPromptActive = value;
    },
    setConsent(value) {
      this.hasConsented = value;
      localStorage.setItem('hasConsented', value);
    },
    setCityData({ name, code, country_code, currency }) {
      this.cityName = name;
      this.cityCode = code;
      this.countryCode = country_code;
      this.latitude = null;
      this.longitude = null;

      persistLocation({
        city: {
          name,
          code,
          country_code,
        },
      }, 'device');
      localStorage.removeItem('latitude');
      localStorage.removeItem('longitude');
      
      // Only override a currency the user has not chosen themselves
      const isManualChoice = localStorage.getItem('currencySource') === 'manual';
      if (!isManualChoice && currency?.currency_code) {
        localStorage.setItem('currency', currency.currency_code);
        localStorage.setItem('preferredCurrency', currency.currency_code);
        window.dispatchEvent(new CustomEvent('currency-updated', {
          detail: { currency: currency.currency_code },
        }));
        console.log('Currency updated from geolocation:', currency.currency_code);
      } else {
        console.log('Preserving manually selected currency');
      }
    },
    setCurrencyData(data) {
      this.currencyData = data;
      localStorage.setItem('currencyData', JSON.stringify(data));
    },
    setNearbyCities(cities) {
      this.nearbyCities = cities;
      localStorage.setItem('nearbyCities', JSON.stringify(cities));
    },
    clearData() {
      this.hasConsented = null;
      this.cityName = null;
      this.cityCode = null;
      this.countryCode = null;
      this.latitude = null;
      this.longitude = null;
      this.currencyData = null;
      this.nearbyCities = [];

      localStorage.removeItem('hasConsented');
      clearStoredLocation();
      localStorage.removeItem('currencyData');
    },
    // New method to check and refresh location for team users
    async refreshTeamUserLocation() {
      try {
        // Request geolocation - similar to requestLocation() in PermissionPopup.vue
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const { latitude, longitude } = position.coords;
              console.log('Refreshing location from device permission');
              this.sendLocationToBackend(latitude, longitude);
            },
            (error) => {
              console.error('Error obtaining location:', error);
            },
            {
              enableHighAccuracy: false,
              timeout: 10000,
              maximumAge: 30000
            }
          );
          return true;
        } else {
          console.error('Geolocation is not supported by this browser.');
          return false;
        }
      } catch (error) {
        console.error('Location refresh failed:', error);
        return false;
      }
    },
    
    // Similar to sendLocationToBackend() in PermissionPopup.vue
    async sendLocationToBackend(latitude, longitude) {
      try {
        const response = await axios.post('/search/closest_location/', {
          latitude,
          longitude,
        });

        if (response.status === 200) {
          const data = response.data;
          console.log('API Response:', data);

          // Extract city data
          const cityData = {
            name: data.name,
            code: data.city_code,
            country_code: data.country_code,
            location: {
              latitude: latitude,
              longitude: longitude
            },
            currency: {
              currency_name: data.currency_name,
              currency_code: data.currency_code,
              currency_position: data.currency_position
            }
          };
          
          // Update city and location data
          this.setCityData(cityData);
          
          // Store nearby cities if available
          if (data.nearby_cities && Array.isArray(data.nearby_cities)) {
            // Store nearby cities in the store or localStorage if needed
            this.setNearbyCities(data.nearby_cities);
            console.log('Nearby cities stored:', data.nearby_cities.length);
          }
          
          // Update last location update time
          localStorage.setItem('lastLocationUpdateTime', Date.now().toString());
          
          return cityData;
        } else {
          console.error('Error from backend:', response.data);
          return null;
        }
      } catch (error) {
        console.error('Network or server error:', error);
        return null;
      }
    },
  },
});
